import { LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { Label } from 'common/components/texts/Texts';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UnitDto } from 'api/units/models/UnitDto';
import UnitsService from 'api/units/UnitsService';
import { Form } from 'common/components/form/Form';

function UnitScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<UnitDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['UNITS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['UNITS_READ'])

    const url = '/backoffice/units';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    useEffect(() => {
        void getData()
    }, [id, type]);

    const getData = async () => {
        try {
            let result: Partial<UnitDto> = { id, name: '' };
            if (id) {
                Loading.show();
                result = await UnitsService.getById(id);
            }
            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get unit information', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/backoffice/units/${typeUrl}/${id}`);
        } else {
            navigate('/backoffice/units');
        }
    }

    const onSubmit = async (model: UnitDto) => {
        try {
            Loading.show();

            if (model && model.id) {
                await UnitsService.update(model)
                // navigateTo('details', id);
                void getData();
            } else if (model) {
                const id = await UnitsService.create(model)
                navigateTo('edit', id);
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the unit with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));

            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false)
            return;
        }

        Loading.show();
        try {
            await UnitsService.remove(form.getValues())
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete unit', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteModal(false)
        }
    }

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t('common.new_title.unit')
        : type === 'edit'
            ? t('common.edit_title.unit')
            : t('common.details_title.unit');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('units.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('units.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && isDetails && <Button fw href={`/backoffice/units/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    <Row>
                        <Col xs={6}>
                            <Label space>{t('units.list.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name' control={form.control}
                                placeholder={t('units.list.name')} disabled={isDetails}
                                rules={{ required: true, maxLength: 250 }}
                                hasError={Boolean(errors.name)} />
                            <InputError error={errors.name} maxLength={250} />
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('units.list.symbol')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='symbol' control={form.control}
                                placeholder={t('units.list.symbol')} disabled={isDetails}
                                rules={{ required: true, maxLength: 250 }}
                                hasError={Boolean(errors.symbol)} />
                            <InputError error={errors.symbol} maxLength={250} />
                        </Col>
                    </Row>
                </PageContainer>
            </Form>
            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    )
}

export default UnitScreen;
